import { UserRole, type User } from '../types/index.js';
import type { LaunchBillingPlanId } from './plans.js';

export type PlanRoleLimitKey = 'teamOwners' | 'coaches' | 'mechanics' | 'parents';

export type PlanRoleLimits = Record<PlanRoleLimitKey, number | null>;

export type PlanRetentionCandidate = {
  userId: string;
  name: string;
  email: string;
  role: UserRole;
};

export type PlanRetentionSlot = {
  key: PlanRoleLimitKey;
  label: string;
  used: number;
  keepLimit: number;
  /** When true the user must pick; when false all listed accounts are paused automatically. */
  requiresSelection: boolean;
  candidates: PlanRetentionCandidate[];
};

export type PlanChangeRetention = {
  required: boolean;
  slots: PlanRetentionSlot[];
};

const ROLE_LIMIT_LABELS: Record<PlanRoleLimitKey, string> = {
  teamOwners: 'Team owner',
  coaches: 'Coaches',
  mechanics: 'Mechanics',
  parents: 'Parents / guardians',
};

const FAMILY_ACCESS_ROLES = new Set<UserRole>([
  UserRole.TEAM_OWNER,
  UserRole.COACH,
  UserRole.MECHANIC,
  UserRole.PARENT,
]);

/** Hard caps per role on launch tiers (null = unlimited on that role). */
export const LAUNCH_PLAN_ROLE_LIMITS: Record<LaunchBillingPlanId, PlanRoleLimits> = {
  privateer: {
    teamOwners: 0,
    coaches: 0,
    mechanics: 0,
    parents: 0,
  },
  family_trackside: {
    teamOwners: 0,
    coaches: 0,
    mechanics: 0,
    parents: 2,
  },
  starter_team: {
    teamOwners: 1,
    coaches: 1,
    mechanics: 2,
    parents: null,
  },
  academy: {
    teamOwners: null,
    coaches: null,
    mechanics: null,
    parents: null,
  },
};

function roleForLimitKey(key: PlanRoleLimitKey): UserRole {
  switch (key) {
    case 'teamOwners':
      return UserRole.TEAM_OWNER;
    case 'coaches':
      return UserRole.COACH;
    case 'mechanics':
      return UserRole.MECHANIC;
    case 'parents':
      return UserRole.PARENT;
    default:
      return UserRole.PARENT;
  }
}

export function resolvePlanRoleLimits(
  planId: LaunchBillingPlanId,
  options?: { isFamilyWorkspace?: boolean },
): PlanRoleLimits {
  const base = LAUNCH_PLAN_ROLE_LIMITS[planId];
  const limits: PlanRoleLimits = {
    teamOwners: base.teamOwners,
    coaches: base.coaches,
    mechanics: base.mechanics,
    parents: base.parents,
  };

  if (options?.isFamilyWorkspace || planId === 'family_trackside') {
    limits.teamOwners = 0;
    limits.coaches = 0;
    limits.mechanics = 0;
    limits.parents = 2;
  }

  return limits;
}

function activeMembers(members: ReadonlyArray<Pick<User, 'id' | 'name' | 'email' | 'role' | 'accountDisabled'>>) {
  return members.filter((member) => member.accountDisabled !== true);
}

function candidatesForRole(
  members: ReadonlyArray<Pick<User, 'id' | 'name' | 'email' | 'role' | 'accountDisabled'>>,
  role: UserRole,
): PlanRetentionCandidate[] {
  return activeMembers(members)
    .filter((member) => member.role === role)
    .map((member) => ({
      userId: member.id,
      name: member.name,
      email: member.email,
      role: member.role,
    }));
}

export function buildPlanChangeRetention(
  planId: LaunchBillingPlanId,
  members: ReadonlyArray<Pick<User, 'id' | 'name' | 'email' | 'role' | 'accountDisabled'>>,
  options?: {
    isFamilyWorkspace?: boolean;
    actingUserId?: string;
  },
): PlanChangeRetention {
  const limits = resolvePlanRoleLimits(planId, options);
  const slots: PlanRetentionSlot[] = [];

  if (options?.isFamilyWorkspace || planId === 'family_trackside') {
    const keepLimit = limits.parents;
    const candidates = activeMembers(members)
      .filter((member) => FAMILY_ACCESS_ROLES.has(member.role))
      .map((member) => ({
        userId: member.id,
        name: member.name,
        email: member.email,
        role: member.role,
      }));

    if (keepLimit != null && candidates.length > keepLimit) {
      slots.push({
        key: 'parents',
        label: 'Family access',
        used: candidates.length,
        keepLimit,
        requiresSelection: keepLimit > 0,
        candidates,
      });
    }

    return {
      required: slots.some((slot) => slot.requiresSelection),
      slots,
    };
  }

  for (const key of Object.keys(limits) as PlanRoleLimitKey[]) {
    const keepLimit = limits[key];
    if (keepLimit == null) {
      continue;
    }

    const role = roleForLimitKey(key);
    const candidates = candidatesForRole(members, role);

    const used = candidates.length;
    if (used <= keepLimit) {
      continue;
    }

    slots.push({
      key,
      label: ROLE_LIMIT_LABELS[key],
      used,
      keepLimit,
      requiresSelection: keepLimit > 0,
      candidates,
    });
  }

  return {
    required: slots.some((slot) => slot.requiresSelection),
    slots,
  };
}

export function validatePlanChangeRetention(
  retention: PlanChangeRetention,
  keepUserIdsBySlot: Partial<Record<PlanRoleLimitKey, string[]>>,
): string | null {
  for (const slot of retention.slots) {
    if (!slot.requiresSelection) {
      continue;
    }

    const selected = keepUserIdsBySlot[slot.key] ?? [];
    const uniqueSelected = Array.from(new Set(selected));

    if (uniqueSelected.length !== slot.keepLimit) {
      return `Select exactly ${slot.keepLimit} ${slot.label.toLowerCase()} to keep on this plan.`;
    }

    const allowedIds = new Set(slot.candidates.map((candidate) => candidate.userId));
    if (uniqueSelected.some((userId) => !allowedIds.has(userId))) {
      return `One or more selected ${slot.label.toLowerCase()} accounts are not eligible.`;
    }
  }

  return null;
}

export function flattenRetentionSelection(keepUserIdsBySlot: Partial<Record<PlanRoleLimitKey, string[]>>) {
  return Array.from(
    new Set(
      Object.values(keepUserIdsBySlot)
        .flat()
        .filter(Boolean),
    ),
  );
}

export function resolveUsersToDisableForRetention(
  retention: PlanChangeRetention,
  keepUserIdsBySlot: Partial<Record<PlanRoleLimitKey, string[]>>,
): Set<string> {
  const keepIds = new Set(flattenRetentionSelection(keepUserIdsBySlot));

  const disableIds = new Set<string>();
  for (const slot of retention.slots) {
    for (const candidate of slot.candidates) {
      if (slot.requiresSelection) {
        if (!keepIds.has(candidate.userId)) {
          disableIds.add(candidate.userId);
        }
      } else {
        disableIds.add(candidate.userId);
      }
    }
  }

  return disableIds;
}
